import { Button, Group, Stack, Tabs, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState, useEffect } from 'react';
import { useGetTranslationsQuery } from '@codezo/graphql';

interface TranslationUpdateFormProps {
	initialValues: any;
	onSubmit: (values: any) => void;
	onCancel: () => void;
	loading?: boolean;
}

export function TranslationUpdateForm({
	initialValues,
	onSubmit,
	onCancel,
	loading,
}: TranslationUpdateFormProps) {
	const [activeTab, setActiveTab] = useState<string>('en');

	// Fetch both English and Danish versions of this translation key
	const { data } = useGetTranslationsQuery({
		variables: {
			language: 'da',
			keys: [initialValues.key],
		},
		fetchPolicy: 'network-only',
	});

	const danishTranslation = data?.getTranslations?.[0];

	const form = useForm({
		initialValues: {
			key: initialValues.key,
			en: initialValues.content || '',
			da: danishTranslation?.content || '',
		},
		validate: {
			en: (value) => (!value ? 'English translation is required' : null),
			da: (value) => (!value ? 'Danish translation is required' : null),
		},
	});

	// Update Danish content when data loads
	useEffect(() => {
		if (danishTranslation) {
			form.setFieldValue('da', danishTranslation.content);
		}
	}, [danishTranslation]);

	const handleSubmit = (values: any) => {
		// Submit updates for both languages
		onSubmit({
			key: values.key,
			translations: [
				{ language: 'en', content: values.en },
				{ language: 'da', content: values.da },
			],
		});
	};

	return (
		<form onSubmit={form.onSubmit(handleSubmit)}>
			<Stack gap="md">
				{/* Key (read-only) */}
				<TextInput
					label="Translation Key"
					value={form.values.key}
					disabled
					description="This key cannot be changed"
				/>

				{/* Translation Tabs */}
				<Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'en')}>
					<Tabs.List>
						<Tabs.Tab value="en">English</Tabs.Tab>
						<Tabs.Tab value="da">Danish</Tabs.Tab>
					</Tabs.List>

					<Tabs.Panel value="en" pt="md">
						<Textarea
							label="English Translation"
							placeholder="Enter English translation"
							required
							autosize
							minRows={3}
							maxRows={10}
							{...form.getInputProps('en')}
						/>
					</Tabs.Panel>

					<Tabs.Panel value="da" pt="md">
						<Textarea
							label="Danish Translation"
							placeholder="Enter Danish translation"
							required
							autosize
							minRows={3}
							maxRows={10}
							{...form.getInputProps('da')}
						/>
					</Tabs.Panel>
				</Tabs>

				{/* Action Buttons */}
				<Group justify="flex-end" mt="md">
					<Button variant="subtle" onClick={onCancel} disabled={loading}>
						Cancel
					</Button>
					<Button type="submit" loading={loading}>
						Update Translation
					</Button>
				</Group>
			</Stack>
		</form>
	);
}
